import { Church } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/ui/Avatar';
import type { NavItem } from './MobileShell';

interface SidebarContentProps {
  navItems: NavItem[];
  churchName?: string;
  user?: { name: string; role: string };
  onNavigate?: (href: string) => void;
  /** Called after any link is chosen (the drawer closes itself with this). */
  onItemChosen?: () => void;
}

/** Logo, grouped navigation, and Account + the user pinned at the bottom. Shared by the desktop sidebar and the phone drawer. */
export const SidebarContent = ({ navItems, churchName, user, onNavigate, onItemChosen }: SidebarContentProps) => {
  const handleClick = (href: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(href);
    }
    onItemChosen?.();
  };

  const link = (item: NavItem) => {
    const Icon = item.icon;
    return (
      <li key={item.href}>
        <a
          href={item.href}
          onClick={(e) => handleClick(item.href, e)}
          className={cn(
            'flex min-h-[44px] items-center gap-3 rounded-control px-3 py-2 text-body font-medium transition-colors',
            item.active ? 'bg-brand-tint text-brand-ink' : 'text-neutral hover:bg-neutral-tint hover:text-ink'
          )}
          aria-current={item.active ? 'page' : undefined}
        >
          <Icon size={18} className="shrink-0" aria-hidden />
          <span className="flex-1">{item.label}</span>
          {!!item.badge && (
            <span
              className="min-w-5 rounded-full bg-wait px-1.5 text-center text-micro font-semibold text-white"
              aria-label={`${item.badge} waiting`}
            >
              {item.badge}
            </span>
          )}
        </a>
      </li>
    );
  };

  // Ungrouped items first, then each named group in order of first appearance; pinned items go to the bottom.
  const main = navItems.filter((i) => !i.pinned);
  const pinned = navItems.filter((i) => i.pinned);
  const ungrouped = main.filter((i) => !i.group);
  const groups = [...new Set(main.filter((i) => i.group).map((i) => i.group!))];

  return (
    <>
      <div className="flex items-center gap-3 border-b border-line px-5 py-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand text-white" aria-hidden>
          <Church size={20} />
        </span>
        <div className="min-w-0">
          <p className="truncate text-card font-semibold text-ink">Church Accounts</p>
          {churchName && <p className="truncate text-micro text-muted">{churchName}</p>}
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" role="navigation" aria-label="Main navigation">
        {ungrouped.length > 0 && <ul className="space-y-1">{ungrouped.map(link)}</ul>}
        {groups.map((g) => (
          <div key={g} className="mt-5">
            <p className="px-3 pb-1 text-micro font-semibold uppercase tracking-wide text-muted">{g}</p>
            <ul className="space-y-1">{main.filter((i) => i.group === g).map(link)}</ul>
          </div>
        ))}
      </nav>

      {(pinned.length > 0 || user) && (
        <div className="border-t border-line px-3 py-3">
          {pinned.length > 0 && <ul className="space-y-1">{pinned.map(link)}</ul>}
          {user && (
            <div className="mt-2 flex items-center gap-3 px-3 py-2">
              <Avatar name={user.name} size="sm" />
              <div className="min-w-0">
                <p className="truncate text-label font-medium text-ink">{user.name}</p>
                <p className="truncate text-micro text-muted">{user.role}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
